import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAngularModule } from '../../../modules/mat-angular.module';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HorizontalSelectorBarComponent, SelectorItem } from '../../horizontal-selector-bar/horizontal-selector-bar.component';
import { FilterConfig, FilterData, SelectedBrand } from '../../../models/generic-models';
import { GenericTemplateCommonService } from '../services/generic-template-common.service';
import { GenericHistoryDialogComponent } from '../../../../dialogs/generic-history-dialog/generic-history-dialog.component';
import { CountryService } from '../../../../services/country.service';



@Component({
  selector: 'app-generic-common-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAngularModule,
    HorizontalSelectorBarComponent
  ],
  templateUrl: './generic-common-filter.component.html',
  styleUrl: './generic-common-filter.component.scss'
})
export class GenericCommonFilterComponent implements OnInit, OnChanges {
  @Input() config!: FilterConfig;
  @Input() error: string | null = null;
  @Input() preselectedBrand: string | null = null;
  @Input() preselectedBrandDomain: string | null = null; // Brand domain/website (from project.description)
  @Input() initialCountry: string | null = null; // Allow parent to set initial country
  @Input() projectId: string | null = null; // Project ID for history dialog
  @Input() projectName: string | null = null; // Project name for history dialog
  @Input() currentVersionName: string | null = null; // Current version name (e.g., v1, v2) to display
  @Input() currentProjectDataId: string | null = null; // Current projectdataId to disable in history dialog
  @Input() initialSelectedEntities: SelectedBrand[] | null = null; // Initial selected entities (for loading from history)

  @Output() filterChange = new EventEmitter<FilterData>();
  @Output() analyzeClick = new EventEmitter<FilterData>();

  // Filter values
  country = 'Global';
  selectedEntities: SelectedBrand[] = [];
  hardReload = false; // Hard reload checkbox state

  // Horizontal selector bar data
  selectorItems: SelectorItem[] = [];

  // Available options
  countryOptions: string[] = [];

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private dialog: MatDialog,
    private router: Router,
    private countryService: CountryService
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
    this.checkHistoryRedirect();
  }

  private checkHistoryRedirect(): void {
    // This method is kept for backward compatibility but refresh is now handled in onHistoryClick
    // Clear any stale refresh flags on component init
    const refreshFlag = sessionStorage.getItem('historyRedirectRefresh');
    if (refreshFlag === 'true') {
      // Check if we actually have projectdataId in URL
      const urlTree = this.router.parseUrl(this.router.url);
      const hasProjectDataId = urlTree.queryParams['projectdataId'];
      if (!hasProjectDataId) {
        // Clear stale flag if no projectdataId
        sessionStorage.removeItem('historyRedirectRefresh');
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Prefer initialSelectedEntities when it has competitors so API gets project.competitors
    if (changes['initialSelectedEntities'] && this.initialSelectedEntities && this.initialSelectedEntities.length > 0) {
      this.handleInitialSelectedEntities();
    }
    if ((changes['preselectedBrand'] && this.preselectedBrand) || 
        (changes['preselectedBrandDomain'] && this.preselectedBrand)) {
      // Don't overwrite with single brand if we already have competitors from initialSelectedEntities
      if (!(this.initialSelectedEntities && this.initialSelectedEntities.length > 1)) {
        this.handlepreselectedBrand();
      }
    }
    
    // Handle initial country change from parent
    // This can be called multiple times - once when component initializes, and again when project loads
    if (changes['initialCountry']) {
      const newCountry = changes['initialCountry'].currentValue;
      if (newCountry) {
        // If countryOptions are already loaded, set it immediately
        if (this.countryOptions.length > 0) {
          // Use case-insensitive matching
          const matchingCountry = this.countryOptions.find(
            opt => opt.toLowerCase() === newCountry.toLowerCase()
          );
          if (matchingCountry) {
            this.country = matchingCountry;
            // Emit filter change to sync with parent
            this.emitFilterChange();
          }
        }
      }
    }
    
    // Handle config changes (country options might be updated)
    if (changes['config'] && this.initialCountry) {
      // Re-check if we can set the country now that config is available
      this.loadInitialData();
    }
  }

  private loadInitialData(): void {
    this.countryOptions = this.config.countryOptions || [];
    if (!this.countryOptions.includes('Global')) {
      this.countryOptions = ['Global', ...this.countryOptions];
    }

    // Set initial country if provided via input and it's available in options
    // Handle case-insensitive matching
    if (this.initialCountry) {
      const matchingCountry = this.countryOptions.find(
        opt => opt.toLowerCase() === this.initialCountry!.toLowerCase()
      );
      if (matchingCountry) {
        this.country = matchingCountry;
      }
    }

    this.initializeHorizontalSelector();
  }

  private initializeHorizontalSelector(): void {
    this.selectorItems = [
      // {
      //   id: 'primary',
      //   name: 'Select your brand',
      //   type: 'primary',
      //   color: '#2196F3',
      //   removable: false
      // }
    ];
  }

  onCountryChange(): void {
    this.selectedEntities = [];
    this.templateCommonService.setCompareClicked(false);
    this.emitFilterChange();
  }

  onHardReloadChange(): void {
    this.emitFilterChange();
  }

  onSelectorSelectionChange(items: SelectorItem[]): void {
    // DON'T update selectorItems here - this creates a circular update loop!
    // The child component manages its own state
    // this.selectorItems = [...items];
    
    const defaultName = 'Select your brand';

    // Filter for selectedEntities (only items with names, excluding empty competitors)
    // But keep all items in selectorItems so empty slots can show input fields
    const uniqueItems = items
      .filter(item => item.name && item.name !== defaultName)
      .filter((item, index, self) =>
        index === self.findIndex(t => t.name === item.name)
      );

    this.selectedEntities = uniqueItems.map(item => {
      const existing = this.selectedEntities.find(
        entity => entity.name.trim().toLowerCase() === item.name.trim().toLowerCase()
      ) as (SelectedBrand & { domain?: string; url?: string }) | undefined;

      return {
        id: parseInt(item.id.replace('competitor-', '')) || 1,
        name: item.name,
        country: this.country,
        ...(existing?.domain ? { domain: existing.domain } : {}),
        ...(existing?.url ? { url: existing.url } : {})
      };
    });

    this.emitFilterChange();
  }

  onSelectorCompareClick(items: SelectorItem[]): void {
    this.templateCommonService.setCompareClicked(true);
    this.onAnalyzeClick();
  }

  onAnalyzeClick(): void {
    if (this.isAnalyzeButtonEnabled()) {
      this.analyzeClick.emit({
        country: this.country,
        selectedEntities: this.selectedEntities,
        hardReload: this.hardReload
      });
    }
  }

  isAnalyzeButtonEnabled(): boolean {
    return this.selectedEntities.length > 0;
  }

  private emitFilterChange(): void {
    this.filterChange.emit({
      country: this.country,
      selectedEntities: this.selectedEntities,
      hardReload: this.hardReload
    });
  }

  private handlepreselectedBrand(): void {
    if (!this.preselectedBrand) {
      return;
    }

    // Remove existing "(YOU)" if present to avoid duplication
    let cleanBrandName = this.preselectedBrand.trim();
    if (cleanBrandName.endsWith(' (YOU)')) {
      cleanBrandName = cleanBrandName.replace(/ \(YOU\)$/, '');
    }

    // Build the display name: "BrandName (YOU)" or "BrandName - www.domain.com (YOU)"
    let displayName = cleanBrandName;
    let fullDomain = '';
    if (this.preselectedBrandDomain && this.preselectedBrandDomain.trim()) {
      // Format domain for display name (with www.) and for tooltip (normalized, no www.)
      let domain = this.preselectedBrandDomain.trim();
      domain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
      const domainWithWww = domain.startsWith('www.') ? domain : `www.${domain}`;
      fullDomain = this.normalizeDomainForDisplay(domainWithWww); // tooltip: e.g. meritas.co.uk
      const domainLower = domainWithWww.toLowerCase();
      const domainWithoutWww = domainLower.replace(/^www\./, '');
      const brandNameLower = cleanBrandName.toLowerCase();
      // Check if brand name already contains the domain (with or without www.)
      const alreadyContainsDomain = brandNameLower.includes(domainLower) || 
                                    brandNameLower.includes(domainWithoutWww);
      
      if (!alreadyContainsDomain) {
        displayName = `${cleanBrandName} - ${domainWithWww} (YOU)`;
      } else {
        displayName = `${cleanBrandName} (YOU)`;
      }
    } else {
      displayName = `${cleanBrandName} (YOU)`;
    }

    const preselectedEntity: SelectedBrand = {
      id: 1,
      name: cleanBrandName, // Store clean name without (YOU) and domain
      country: this.country
    };

    this.selectedEntities = [preselectedEntity];

    this.selectorItems = [
      {
        id: 'primary',
        name: displayName,
        type: 'primary',
        color: '#4CAF50',
        domain: fullDomain,
        removable: false
      }
    ];

    this.emitFilterChange();
  }

  private handleInitialSelectedEntities(): void {
    if (!this.initialSelectedEntities || this.initialSelectedEntities.length === 0) {
      return;
    }

    // Update selectedEntities
    this.selectedEntities = [...this.initialSelectedEntities];

    // Build selectorItems from selectedEntities; set domain for tooltips (brand + competitors)
    const colorPalette = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#607D8B', '#E91E63', '#FF5722', '#795548', '#9E9E9E'];
    this.selectorItems = this.initialSelectedEntities.map((entity, index) => {
      if (index === 0) {
        const displayName = entity.name.includes('(YOU)') ? entity.name : `${entity.name} (YOU)`;
        const domain = (entity as SelectedBrand & { domain?: string }).domain || this.preselectedBrandDomain || undefined;
        const domainDisplay = domain ? this.normalizeDomainForDisplay(domain) : undefined;
        return {
          id: 'primary',
          name: displayName,
          type: 'primary',
          color: '#4CAF50',
          domain: domainDisplay,
          removable: false
        };
      } else {
        const entityWithDomain = entity as SelectedBrand & { domain?: string; url?: string };
        const rawDomain = entityWithDomain.domain || (entityWithDomain.url ? this.normalizeDomainForDisplay(entityWithDomain.url) : undefined);
        const domainDisplay = rawDomain ? this.normalizeDomainForDisplay(rawDomain) : undefined;
        return {
          id: `competitor-${index}`,
          name: entity.name,
          type: 'competitor',
          color: colorPalette[index % colorPalette.length],
          domain: domainDisplay,
          removable: true
        };
      }
    });

    this.emitFilterChange();
  }

  /** Normalize domain for tooltip (strip protocol and www). */
  private normalizeDomainForDisplay(domain: string): string {
    if (!domain || typeof domain !== 'string') return '';
    return domain.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '').replace(/^www\./, '') || domain.trim();
  }

  selectCountry(country: string): void {
    this.country = country;
    this.onCountryChange();
  }

  getCountryFlag(country: string): string {
    return this.countryService.getCountryIcon(country);
  }

  onHistoryClick(): void {
    if (!this.projectId || !this.projectName) {
      console.warn('Project ID or Project Name not available for history dialog');
      return;
    }

    const dialogRef = this.dialog.open(GenericHistoryDialogComponent, {
      width: '80%',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        projectId: this.projectId,
        projectName: this.projectName,
        currentProjectDataId: this.currentProjectDataId, // Pass current version ID to disable it
        openedFromAnalysisPage: !!this.currentProjectDataId // Flag to indicate we're on analysis page
      }
    });

    // Navigation and reload are handled by the dialog component using window.location.href
    // No need to handle refresh here as dialog forces full page reload
    dialogRef.afterClosed().subscribe(() => {
      // Dialog handles navigation and reload internally
    });
  }
}

